/* eslint-disable react/display-name */
import { Button, Card, CardActions, CardContent, Chip, colors, Divider, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import clsx from 'clsx';
import { RenderGuard } from 'components';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState } from 'react';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[50],
    width: "90%",
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  cancelButton: {
    marginLeft: 'auto'
  },
  confirmButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  header: {
    marginBottom: theme.spacing(3)
  },
  chip: {
    margin: theme.spacing(1),
  },
  chips_container: {
    padding: theme.spacing(2)
  }
}));

const every_options = [
  { label: "Day", value: "day" },
  { label: "Month", value: "month" },
  { label: "Week", value: "week" },
];

const week_options = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const monthly_options = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: 5, label: "5th" },
  { value: 6, label: "6th" },
  { value: 7, label: "7th" },
  { value: 8, label: "8th" },
  { value: 9, label: "9th" },
  { value: 10, label: "10th" },
  { value: 11, label: "11th" },
  { value: 12, label: "12th" },
  { value: 13, label: "13th" },
  { value: 14, label: "14th" },
  { value: 15, label: "15th" },
  { value: 16, label: "16th" },
  { value: 17, label: "17th" },
  { value: 18, label: "18th" },
  { value: 19, label: "19th" },
  { value: 20, label: "20th" },
  { value: 21, label: "21st" },
  { value: 22, label: "22nd" },
  { value: 23, label: "23rd" },
  { value: 24, label: "24th" },
  { value: 25, label: "25th" },
  { value: 26, label: "26th" },
  { value: 27, label: "27th" },
  { value: 28, label: "28th" },
  { value: 29, label: "29th" },
  { value: 30, label: "30th" },
  { value: 31, label: "31st" },
];

const AddSlot = forwardRef((props, ref) => {
  const {
    slot,
    onDelete,
    onCancel,
    onAdd,
    onEdit,
    className,
    ...rest
  } = props;

  const classes = useStyles();

  const defaultSlot = {
    every: "day",
    start: moment().format("YYYY-MM-DD"),
    slot_date: moment().format("YYYY-MM-DD"),
    end: moment().format("YYYY-MM-DD"),
    start_time: moment().format("HH:mm"),
    end_time: moment().add(1, "hours").format("HH:mm")
  };

  const [values, setValues] = useState(slot || defaultSlot);
  const [type, setType] = useState("single");
  const [repeatOptions, setRepeatOptions] = useState([]);
  const [slots, setSlots] = useState([]);

  const mode = slot ? 'edit' : 'add';

  const handleFieldChange = e => {
    e.persist();
    let value = e.target.value;
    setValues(values => ({
      ...values,
      [e.target.name]:
        e.target.type === 'checkbox' ? e.target.checked : value
    }));
  };

  const handleAdd = () => {
    if (!slots.length) {
      return;
    }

    onAdd(slots);
  };

  const handleEdit = () => {
    if (!values.title || !values.desc) {
      return;
    }

    onEdit(values);
  };

  useEffect(() => {
    if (values.every === "day") {
      setRepeatOptions([]);
    } else if (values.every === "week") {
      setRepeatOptions(week_options);
      setValues(values => ({ ...values, repeat: 0 }));
    } else if (values.every === "month") {
      setRepeatOptions(monthly_options);
      setValues(values => ({ ...values, repeat: 1 }));
    }
  }, [values.every]);

  useEffect(() => {
    let { every, start, end, repeat, start_time, end_time, slot_date } = values;

    let new_slots = [];
    if (type === "recurring") {
      start = moment(start);
      end = moment(end).endOf("day");
      if (every && start && end && start_time && end_time
        && (every !== "day" ? repeat !== null : true) && end.diff(start) > 0) {
        let slot_start = moment(start).add(start_time.slice(0, 2), "hours").add(start_time.slice(3, 5), "minutes");
        let slot_end = moment(start).add(end_time.slice(0, 2), "hours").add(end_time.slice(3, 5), "minutes");
        if (slot_end.diff(slot_start) > 0) {
          let duration = slot_end.diff(slot_start, "seconds");
          let diff = 0;
          switch (values.every) {
            case "day":
              diff = end.diff(start, "days");
              for (let i = 0; i <= diff; i++) {
                new_slots.push({
                  start_at: slot_start.format("YYYY-MM-DD HH:mm:ss"),
                  duration
                });
                slot_start.add(1, "days");
              }
              break;
            case "month":
              diff = Math.ceil(end.diff(start, "months", true));
              for (let i = 0; i <= diff + 1; i++) {
                // see if there's date in current month;
                if (slot_start.daysInMonth() >= repeat) {
                  // set date
                  slot_start.date(repeat);
                  // see if set date is before start time
                  if (slot_start.isAfter(start) && slot_start.isBefore(end)) {
                    new_slots.push({
                      start_at: slot_start.format("YYYY-MM-DD HH:mm:ss"),
                      duration
                    });
                  }
                }
                slot_start.add(1, "months");
              }
              break;
            case "week":
              diff = Math.ceil(end.diff(start, "weeks", true));
              for (let i = 0; i <= diff + 1; i++) {
                // set day
                slot_start.day(repeat);
                // see if set date is before start time
                if (slot_start.isAfter(start) && slot_start.isBefore(end)) {
                  new_slots.push({
                    start_at: slot_start.format("YYYY-MM-DD HH:mm:ss"),
                    duration
                  });
                }
                slot_start.add(1, "weeks");
              }
              break;
            default: break;
          }
        }

      }
    } else {
      if (slot_date && start_time && end_time) {
        slot_date = moment(slot_date);
        let slot_start = moment(slot_date).add(start_time.slice(0, 2), "hours").add(start_time.slice(3, 5), "minutes");
        let slot_end = moment(slot_date).add(end_time.slice(0, 2), "hours").add(end_time.slice(3, 5), "minutes");
        let duration = slot_end.diff(slot_start, "seconds");
        if (duration > 0) {
          new_slots = [{
            start_at: slot_start.format("YYYY-MM-DD HH:mm:ss"),
            duration
          }]
        }
      }
    }
    setSlots(new_slots);
  }, [values.every, values.repeat, values.start_time, values.end_time, values.start, values.end, values.slot_date, type, values]);

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
      ref={ref}
    >
      <form>
        <CardContent>
          <ToggleButtonGroup
            exclusive
            onChange={(event, value) => value && setType(value)}
            size="small"
            value={type}
            variant="outlined"
          >
            <ToggleButton
              color="primary"
              value="single"
            >
              Single
        </ToggleButton>
            <ToggleButton value="recurring">Recurring</ToggleButton>
          </ToggleButtonGroup>
          <Typography
            align="center"
            gutterBottom
            variant="h3"
            className={classes.header}
          >
            {mode === 'add' ? 'Add Slot' : 'Edit Slot'}
          </Typography>
          <Grid container spacing={3}>
            <RenderGuard renderIf={type === "recurring"}>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Repeat from"
                  name="start"
                  onChange={handleFieldChange}
                  type="date"
                  variant="outlined"
                  value={values.start}
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Repeat till"
                  name="end"
                  onChange={handleFieldChange}
                  type="date"
                  variant="outlined"
                  value={values.end}
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Repeat Every"
                  name="every"
                  onChange={handleFieldChange}
                  SelectProps={{ native: true }}
                  value={values.every}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  select
                >
                  {every_options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </TextField>
              </Grid>
              <Grid item md={6} xs={6}>
                <RenderGuard renderIf={values.every !== "day"}>
                  <TextField
                    fullWidth
                    label="Repeat On"
                    name="repeat"
                    onChange={handleFieldChange}
                    SelectProps={{ native: true }}
                    value={values.repeat}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    select
                  >
                    {repeatOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </TextField>
                </RenderGuard>
              </Grid>
            </RenderGuard>
            <RenderGuard renderIf={type === "single"}>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Date"
                  name="slot_date"
                  onChange={handleFieldChange}
                  type="date"
                  variant="outlined"
                  value={values.slot_date}
                />
              </Grid>
              <Grid item md={6} xs={6} />
            </RenderGuard>
            <Grid item md={6} xs={6}>
              <TextField
                className={classes.field}
                fullWidth
                label="Start time"
                name="start_time"
                onChange={handleFieldChange}
                type="time"
                variant="outlined"
                value={values.start_time}
              />
            </Grid>
            <Grid item md={6} xs={6}>
              <TextField
                className={classes.field}
                fullWidth
                label="End time"
                name="end_time"
                onChange={handleFieldChange}
                type="time"
                variant="outlined"
                value={values.end_time}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <RenderGuard renderIf={slots.length}>
          <div className={classes.chips_container}>
            <Typography
              variant="h4"
              className={classes.header}
            >
              Slots to be added ({slots.length})
            </Typography>
            {slots.map((slot, index) => (
              <Chip size="small" key={index} label={`${moment(slot.start_at).format("DD/MM/YYYY")}`} className={classes.chip} />
            ))}
          </div>
          <Divider />
        </RenderGuard>
        <CardActions>
          <Button
            className={classes.cancelButton}
            onClick={onCancel}
            variant="contained"
          >
            Cancel
          </Button>
          {mode === 'add' ? (
            <Button
              className={classes.confirmButton}
              onClick={handleAdd}
              variant="contained"
            >
              Add
            </Button>
          ) : (
              <Button
                className={classes.confirmButton}
                onClick={handleEdit}
                variant="contained"
              >
                Save
            </Button>
            )}
        </CardActions>
      </form>
    </Card>
  );
});

AddSlot.propTypes = {
  className: PropTypes.string,
  slot: PropTypes.object,
  onAdd: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func
};

export default AddSlot;
